// core
import React, { ReactElement } from "react";
import { Switch, Route as RouterRoute, Router } from "react-router-dom";
import SessionContainer from "./containers/SessionContainer";
import ApplyRedirectPage from "./pages/ApplyRedirectPage";
import ErrorPage from "./pages/ErrorPage";
import * as Sentry from "@sentry/react";
import { SetUpErrorLogging } from "../common/hooks/useErrorLogger";
import { ProductsSelectPage } from "./pages/ProductsSelectPage/ProductsSelectPage";
import StyleGuideRoutes from "./routes/StyleGuideRoutes";
import RouteRedirects from "./routes/RouteRedirects";
import ProductRoutes from "./routes/ProductRoutes";
import FamilySettingsPage from "./pages/FamilySettingsPage/FamilySettingsPage";
import DigitalAdviceRoutes from "../digital-advice/routes/DigitalAdviceRoutes";
import { withSplitFactory } from "@splitsoftware/splitio-react";
import WizardPage from "./pages/WizardPage";
import ProductPage from "./pages/ProductPage/ProductPage";
import ProductPageContainer from "./containers/ProductPageContainer";
import QuoteTableSummaryPage from "./components/Quotes/QuoteTableSummary/QuoteTableSummaryPage";
import ViewQuotePage from "./pages/ViewQuotePage";
import StreamlineSettingsPage from "./pages/StreamlineSettingsPage";
import AppFormRoutes from "../app-form/routes/AppFormRoutes";

// We need to wrap the Route component from react-router-dom so Sentry can read things variable URLs
const Route = Sentry.withSentryRouting(RouterRoute);

const history = SetUpErrorLogging();

function App(): ReactElement {
    return (
        <Router history={history}>
            <Switch>
                {/* This handles the hand-off from Multi-compare to the app form */}
                <Route exact path="/apply/form" component={ApplyRedirectPage} />

                <Route path="/styleguide">
                    <StyleGuideRoutes base="/styleguide" />
                </Route>

                <Route path="/quote/:groupId/:isFromCrm?">
                    <ViewQuotePage />
                </Route>

                <Route path="/digital-advice">
                    <DigitalAdviceRoutes base="/digital-advice" />
                </Route>

                <Route path="/app-form">
                    <AppFormRoutes base="/app-form" />
                </Route>

                {/* Anything in here, is where we need to load the group */}
                <Route path="*">
                    <SessionContainer>
                        <Switch>
                            <Route exact path="/">
                                <RouteRedirects />
                            </Route>

                            <Route exact path="/products/edit">
                                <ProductsSelectPage isEditable />
                            </Route>

                            <Route exact path="/products">
                                <ProductsSelectPage />
                            </Route>

                            <Route exact path="/family">
                                <FamilySettingsPage />
                            </Route>

                            <Route path={"/family-settings/:currentProduct"}>
                                <StreamlineSettingsPage />
                            </Route>

                            <Route exact path="/settings/wizard/:currentProduct">
                                <WizardPage />
                            </Route>

                            <Route exact path={`/result/card/:key`}>
                                <ProductPageContainer
                                    hasFamilyRedirect
                                    hasSettingsRedirect>
                                    <QuoteTableSummaryPage />
                                </ProductPageContainer>
                            </Route>

                            <Route exact path="/results">
                                <ProductPage />
                            </Route>

                            <ProductRoutes />

                            <Route path="*">
                                <ErrorPage />
                            </Route>
                        </Switch>
                    </SessionContainer>
                </Route>
            </Switch>
        </Router>
    );
}

export default window.splitioFactory
    ? withSplitFactory(null, window.splitioFactory)(App)
    : App;
